import type { FichaOdoo } from './filas'

/**
 * Elegir la ficha de Odoo de un cliente cuyo NIF tiene varias con nombres
 * distintos. Bajo `P1204000B` hay cuatro dependencias del Ayuntamiento de
 * Castellon, y elegir por antiguedad mandaria el pedido al departamento
 * equivocado, con su albaran a la puerta de otro edificio.
 *
 * Va **dentro de la tabla**, en la fila del cliente, y no en un dialogo aparte:
 * quien revisa una lista de veinte tiene que poder resolver los dos ambiguos
 * sin salir de la lista ni perder de vista al resto.
 *
 * Cada opcion lleva nombre **y direccion**. Los cuatro candidatos del
 * Ayuntamiento se llaman casi igual; lo que los distingue es la calle.
 */

export function direccionDe(ficha: FichaOdoo): string {
  return [ficha.calle, ficha.codigo_postal, ficha.ciudad].filter(Boolean).join(', ')
}

function etiquetaOpcion(ficha: FichaOdoo): string {
  const direccion = direccionDe(ficha)
  return direccion ? `${ficha.nombre} - ${direccion}` : `${ficha.nombre} - sin direccion`
}

interface Props {
  /** Nombre del cliente: da nombre accesible unico a cada selector de la tabla. */
  cliente: string
  candidatos: FichaOdoo[]
  elegida: number | null
  onElegir: (id: number | null) => void
}

const CLASE_CAMPO =
  'w-full min-w-56 rounded-lg border border-borde-fuerte bg-superficie px-2 py-1.5 text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo'

export function SelectorFicha({ cliente, candidatos, elegida, onElegir }: Props) {
  const ficha = candidatos.find((candidata) => candidata.id === elegida) ?? null

  return (
    <div className="flex flex-col gap-1">
      <select
        aria-label={`Ficha de Odoo de ${cliente}`}
        value={elegida === null ? '' : String(elegida)}
        onChange={(evento) => onElegir(evento.target.value ? Number(evento.target.value) : null)}
        // El clic no debe llegar a la fila: seleccionarla desplegaria el detalle
        // justo cuando se esta intentando abrir el desplegable.
        onClick={(evento) => evento.stopPropagation()}
        onKeyDown={(evento) => evento.stopPropagation()}
        className={`${CLASE_CAMPO} ${elegida === null ? 'border-atencion-anillo' : ''}`}
      >
        <option value="">Elegir entre {candidatos.length} fichas</option>
        {candidatos.map((candidata) => (
          <option key={candidata.id} value={candidata.id}>
            {etiquetaOpcion(candidata)}
          </option>
        ))}
      </select>
      {ficha ? (
        // La direccion tambien fuera del desplegable: cerrado, el navegador
        // recorta la opcion elegida y la calle es justo lo que se pierde.
        <span className="text-xs text-tinta-apagada">{direccionDe(ficha) || 'Sin direccion'}</span>
      ) : null}
    </div>
  )
}

/** La ficha ya decidida, cuando no hay nada que elegir. */
export function FichaResuelta({ ficha }: { ficha: FichaOdoo }) {
  return (
    <div className="flex flex-col">
      <span className="text-sm text-tinta">{ficha.nombre}</span>
      <span className="text-xs text-tinta-apagada">{direccionDe(ficha) || 'Sin direccion'}</span>
    </div>
  )
}
